import React, { useMemo } from "react";
import { Select, Row } from "antd";
import { noop } from "lodash";
import { PAGE_SIZES, IPagination } from "./constants";
import WhiteSpace from "components/base/WhiteSpace";
import { numberFormatter } from "utils/formatters";
import { useTheme } from "themes/useTheme";
import {
  StepBackwardFilled,
  CaretLeftFilled,
  CaretRightFilled,
  StepForwardFilled,
} from "@ant-design/icons";
import useSizeMediaQuery from "hooks/useSizeMediaQuery";
import { useTranslation } from "react-i18next";

interface IProps {
  small?: boolean;
  value: IPagination;
  onChange: (val: IPagination) => void;
  total: number;
}

const Pagination = (props: IProps) => {
  const { value, onChange, total } = props;
  const { pageSize, pageIndex } = value;

  const size = useSizeMediaQuery();

  const isMobile = useMemo(() => ["xs", "sm"].includes(size), [size]);

  const handlePaginationChange = React.useCallback(
    (payload: Partial<IPagination>) => {
      onChange({
        ...value,
        ...payload,
      });
    },
    [value, onChange]
  );

  const handlePageSizeChange = React.useCallback(
    (size: number) => {
      if (size * (pageIndex + 1) > total) {
        handlePaginationChange({ pageSize: size, pageIndex: 0 });
      } else handlePaginationChange({ pageSize: size });
    },
    [handlePaginationChange, pageIndex, total]
  );

  const menu = React.useMemo(() => {
    const pageSizes =
      total < 10 ? [10] : PAGE_SIZES.filter((size: number) => size <= total);
    return (
      <Select
        defaultValue={pageSize}
        onChange={handlePageSizeChange}
        style={{ minWidth: 70 }}
      >
        {pageSizes.map((size: number) => (
          <Select.Option value={size} key={size}>
            {size}
          </Select.Option>
        ))}
      </Select>
    );
  }, [total, handlePageSizeChange, pageSize]);

  const totalPage = Math.ceil(total / pageSize);

  const formattedTotal = useMemo(() => numberFormatter(total), [total]);

  const from = useMemo(() => numberFormatter(pageSize * pageIndex + 1), [
    pageSize,
    pageIndex,
  ]);

  const to = useMemo(
    () => numberFormatter(Math.min(pageSize * (pageIndex + 1), total)),
    [pageIndex, pageSize, total]
  );

  const { t } = useTranslation();

  return (
    <Row
      align="middle"
      style={{
        minHeight: 40,
      }}
    >
      {!isMobile && <span>{t("components.pagination.show_rows")}</span>}
      <WhiteSpace horizontal size={isMobile ? "xs" : "sm"} />
      {menu}
      <WhiteSpace horizontal size={isMobile ? "md" : "lg"} />
      <div>
        {t("components.pagination.summary", {
          from,
          to,
          total: formattedTotal,
        })}
      </div>
      <WhiteSpace horizontal size={isMobile ? "sm" : "md"} />
      <div style={{ display: "flex", flexDirection: "row" }}>
        <PaginationIcon
          component={StepBackwardFilled}
          disabled={pageIndex <= 0}
          onClick={() => handlePaginationChange({ pageIndex: 0 })}
        />
        <PaginationIcon
          component={CaretLeftFilled}
          disabled={pageIndex <= 0}
          onClick={() => handlePaginationChange({ pageIndex: pageIndex - 1 })}
        />
        <PaginationIcon
          component={CaretRightFilled}
          disabled={pageIndex >= totalPage - 1}
          onClick={() => handlePaginationChange({ pageIndex: pageIndex + 1 })}
        />
        <PaginationIcon
          component={StepForwardFilled}
          disabled={pageIndex >= totalPage - 1}
          onClick={() => handlePaginationChange({ pageIndex: totalPage - 1 })}
        />
      </div>
    </Row>
  );
};

interface IPaginationIconProps {
  component: React.FC<any>;
  onClick?: () => void;
  disabled?: boolean;
}

const PaginationIcon = (props: IPaginationIconProps) => {
  const { component: Component, onClick = noop, disabled = false } = props;

  const { themeVariables } = useTheme();

  return (
    <Component
      onClick={disabled ? noop : onClick}
      style={{
        fontSize: 20,
        paddingLeft: 5,
        paddingRight: 5,
        color: disabled
          ? themeVariables.disabled_color
          : themeVariables.font_color,
      }}
    />
  );
};

export default React.memo(Pagination);
